import { useId } from 'react';
import { Ticket } from 'lucide-react';

import {
  ColorField,
  useBackgroundSwatches,
} from '@/components/app-editor/color-field';
import { ContrastBadge } from '@/components/coupon-banner/contrast-badge';
import { couponContrast } from '@/components/coupon-banner/coupon-studio';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MinimalTiptap } from '@/components/ui/shadcn-io/minimal-tiptap';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  formatContrastRatio,
  pickTextColor,
} from '@/lib/app-editor/color-contrast';
import {
  COUPON_TEXT_MAX,
  COUPON_TEXT_MIN,
  applyBackground,
  getBannerPlainText,
  type CouponBannerForm,
  type CouponFieldError,
  type CouponPosition,
} from '@/lib/app-editor/coupon-banner-form';
import { cn } from '@/lib/utils';

// The brand book's field language (as in the auth screens): small uppercase
// labels, 52px fields with a blue focus ring.
const FIELD_LABEL =
  'text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B6371]';
const HINT = 'text-[13px] leading-normal text-[#4B5361]';
const ERROR = 'text-sm text-destructive';
const FOCUS_RING =
  'focus-within:border-[#155DFC] focus-within:ring-4 focus-within:ring-[#155DFC]/15';
// The 4,5:1 minimum on the meter's 21:1 scale.
const AA_MARK = `${(4.5 / 21) * 100}%`;

interface PanelProps {
  form: CouponBannerForm;
  onChange: (form: CouponBannerForm) => void;
}

export interface CouponTextPanelProps extends PanelProps {
  errors: Partial<Record<'text' | 'couponCode', CouponFieldError>>;
  /** After a publish attempt: errors become visible. */
  showErrors: boolean;
}

export function CouponTextPanel({
  form,
  onChange,
  errors,
  showErrors,
}: CouponTextPanelProps) {
  const { LL } = useI18nContext();
  const textMessage = {
    textTooShort: LL.couponEditor.errors.textTooShort({ min: COUPON_TEXT_MIN }),
    textTooLong: LL.couponEditor.errors.textTooLong({ max: COUPON_TEXT_MAX }),
    couponRequired: LL.couponEditor.errors.couponRequired(),
  };

  return (
    <div className="space-y-[26px]">
      <div className="space-y-2.5">
        <Label className={FIELD_LABEL}>
          {LL.appCustomizationPage.couponBanner.richTextLabel()}
        </Label>
        <MinimalTiptap
          content={form.bannerText}
          onChange={(bannerText) => onChange({ ...form, bannerText })}
          placeholder={LL.appCustomizationPage.couponBanner.richTextPlaceholder()}
          // Grows with the text and leaves room for the counter: a fixed
          // height used to cut the second line off.
          className={cn(
            'rounded-xl border-[#DDE1E7] bg-white text-base [&_.ProseMirror-focused]:outline-none',
            FOCUS_RING
          )}
          editorClassName="min-h-[72px] px-3.5 pb-7 pt-3 leading-normal"
          functionConfig={{ bold: true, undoRedo: true }}
          limit={COUPON_TEXT_MAX}
          oneLine
        />
        <p className={HINT}>
          {LL.appCustomizationPage.couponBanner.richTextDescription()}
        </p>
        {showErrors && errors.text && (
          <p role="alert" className={ERROR}>
            {textMessage[errors.text]}
          </p>
        )}
      </div>

      <div className="space-y-2.5">
        <Label htmlFor="coupon-code" className={FIELD_LABEL}>
          {LL.appCustomizationPage.couponBanner.couponCodeLabel()}
        </Label>
        <div className="relative">
          <Ticket
            aria-hidden
            strokeWidth={1.5}
            className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-[#4B5361]"
          />
          <Input
            id="coupon-code"
            value={form.couponCode}
            onChange={(e) => onChange({ ...form, couponCode: e.target.value })}
            placeholder="APP10"
            aria-invalid={(showErrors && !!errors.couponCode) || undefined}
            className="h-[52px] rounded-xl border-[#DDE1E7] pl-12 text-base font-semibold tracking-[0.06em] text-[#15191E] placeholder:font-normal placeholder:text-[#6B7280] focus-visible:border-[#155DFC] focus-visible:ring-4 focus-visible:ring-[#155DFC]/15 focus-visible:ring-offset-0 md:text-base"
          />
        </div>
        <p className={HINT}>
          {LL.appCustomizationPage.couponBanner.couponCodeDescription()}
        </p>
        {showErrors && errors.couponCode && (
          <p role="alert" className={ERROR}>
            {textMessage[errors.couponCode]}
          </p>
        )}
      </div>
    </div>
  );
}

const SEGMENT =
  'h-10 rounded-[9px] text-sm font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#155DFC] max-md:min-h-touch';
const SEGMENT_ON =
  'bg-white text-[#15191E] shadow-[0_1px_2px_rgba(0,0,0,0.08)]';
const SEGMENT_OFF = 'text-[#4B5361] hover:text-[#15191E]';

export function CouponColorPanel({ form, onChange }: PanelProps) {
  const { LL, locale } = useI18nContext();
  const swatches = useBackgroundSwatches(true);
  const contrast = couponContrast(
    form.backgroundColorHex,
    form.foregroundColorHex
  );
  const isAuto = form.textColorMode === 'auto';

  const colorProps = {
    swatches,
    otherLabel: LL.appEditor.color.other(),
    invalidHexMessage: LL.appEditor.color.invalidHex(),
  };

  return (
    <div className="space-y-[26px]">
      <div className="space-y-3">
        <Label className={FIELD_LABEL}>
          {LL.appCustomizationPage.couponBanner.backgroundColorLabel()}
        </Label>
        <ColorField
          {...colorProps}
          label={LL.appCustomizationPage.couponBanner.backgroundColorLabel()}
          value={form.backgroundColorHex}
          onChange={(hex) => onChange(applyBackground(form, hex))}
        />
      </div>

      <div className="space-y-3">
        <Label className={FIELD_LABEL}>
          {LL.couponEditor.color.textAndButton()}
        </Label>
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-[#F1F3F6] p-1">
          <button
            type="button"
            aria-pressed={isAuto}
            className={cn(SEGMENT, isAuto ? SEGMENT_ON : SEGMENT_OFF)}
            onClick={() =>
              onChange({
                ...form,
                textColorMode: 'auto',
                foregroundColorHex: pickTextColor(form.backgroundColorHex),
              })
            }
          >
            {LL.couponEditor.color.auto()}
          </button>
          <button
            type="button"
            aria-pressed={!isAuto}
            className={cn(SEGMENT, isAuto ? SEGMENT_OFF : SEGMENT_ON)}
            onClick={() => onChange({ ...form, textColorMode: 'manual' })}
          >
            {LL.couponEditor.color.manual()}
          </button>
        </div>
        {!isAuto && (
          <ColorField
            {...colorProps}
            label={LL.appCustomizationPage.couponBanner.foregroundColorLabel()}
            value={form.foregroundColorHex}
            onChange={(hex) =>
              onChange({
                ...form,
                foregroundColorHex: hex,
                textColorMode: 'manual',
              })
            }
          />
        )}
      </div>

      <div
        data-testid="coupon-contrast"
        className={cn(
          'space-y-3.5 rounded-2xl p-[18px]',
          contrast.readable
            ? 'bg-[#F6F8FC] shadow-[inset_0_0_0_1px_rgba(21,93,252,0.12)]'
            : 'bg-[#FFF6F5] shadow-[inset_0_0_0_1px_rgba(168,35,27,0.18)]'
        )}
      >
        <div className="flex items-end justify-between gap-3">
          <span className="flex flex-col gap-1">
            <span className={FIELD_LABEL}>
              {LL.couponEditor.color.contrast()}
            </span>
            <span className="text-4xl font-medium leading-none tracking-[-0.03em] text-[#15191E] tabular-nums">
              {formatContrastRatio(contrast.ratio, locale)}
            </span>
          </span>
          <ContrastBadge readable={contrast.readable} />
        </div>
        <div aria-hidden className="relative h-1 rounded-sm bg-[#E3E6EA]">
          <div
            className={cn(
              'absolute inset-y-0 left-0 rounded-sm',
              contrast.readable ? 'bg-[#155DFC]' : 'bg-[#A8231B]'
            )}
            style={{ width: `${contrast.meter * 100}%` }}
          />
          <div
            className="absolute -top-1 h-3 w-px bg-[#15191E]"
            style={{ left: AA_MARK }}
          />
        </div>
        {contrast.readable ? (
          <p className={HINT}>{LL.couponEditor.color.readable()}</p>
        ) : (
          <p role="alert" className="text-[13px] leading-normal text-[#A8231B]">
            {LL.couponEditor.color.lowContrast()}
          </p>
        )}
      </div>
    </div>
  );
}

/** A choice card: `label` names the button, `hint` describes it. */
function ChoiceCard({
  pressed,
  onClick,
  label,
  hint,
  className,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  label: string;
  hint: string;
  className?: string;
  children: React.ReactNode;
}) {
  const hintId = useId();

  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-describedby={hintId}
      onClick={onClick}
      className={cn(
        'w-full rounded-2xl text-[#15191E] outline-none transition-[background-color,box-shadow] focus-visible:ring-2 focus-visible:ring-[#155DFC] focus-visible:ring-offset-2',
        pressed
          ? 'bg-white shadow-[inset_0_0_0_2px_#155DFC]'
          : 'bg-[#F6F7F9] shadow-[inset_0_0_0_1px_rgba(21,25,30,0.08)] hover:bg-white',
        className
      )}
    >
      {children}
      <span className="flex flex-col gap-0.5">
        <span className="text-[15px] font-semibold">{label}</span>
        {/* Out of the name ("Topo"), read as the description. */}
        <span id={hintId} aria-hidden className="text-[12.5px] text-[#4B5361]">
          {hint}
        </span>
      </span>
    </button>
  );
}

/** A phone outline with the banner (in its colours) at the top or bottom. */
function MiniPhone({
  position,
  backgroundColorHex,
}: {
  position: CouponPosition;
  backgroundColorHex: string;
}) {
  const bar = (
    <span
      className="h-[9px] shrink-0 max-md:h-[7px]"
      style={{ backgroundColor: backgroundColorHex }}
    />
  );
  return (
    <span
      aria-hidden
      className="flex h-[140px] w-[74px] flex-col overflow-hidden rounded-2xl border-[3px] border-[#1C1C1E] bg-white max-md:h-[104px] max-md:w-14 max-md:rounded-[13px]"
    >
      <span className="h-3 shrink-0 max-md:h-[9px]" />
      {position === 'top' && bar}
      <span className="mx-1.5 mt-1.5 h-1.5 rounded-[3px] bg-[#E9EBEE]" />
      <span className="mx-1.5 mt-[5px] h-6 rounded bg-[#E9EBEE]" />
      <span className="flex-1" />
      {position === 'bottom' && bar}
      <span className="h-3.5 shrink-0 border-t border-[#EEEEEE] max-md:h-[11px]" />
    </span>
  );
}

export function CouponPositionPanel({ form, onChange }: PanelProps) {
  const { LL } = useI18nContext();
  const options: { value: CouponPosition; label: string; hint: string }[] = [
    {
      value: 'top',
      label: LL.appCustomizationPage.couponBanner.top(),
      hint: LL.couponEditor.position.topHint(),
    },
    {
      value: 'bottom',
      label: LL.appCustomizationPage.couponBanner.bottom(),
      hint: LL.couponEditor.position.bottomHint(),
    },
  ];

  return (
    <div className="space-y-3">
      <Label className={FIELD_LABEL}>
        {LL.appCustomizationPage.couponBanner.positionLabel()}
      </Label>
      <div className="grid grid-cols-2 gap-3">
        {options.map((option) => (
          <ChoiceCard
            key={option.value}
            pressed={form.position === option.value}
            onClick={() => onChange({ ...form, position: option.value })}
            label={option.label}
            hint={option.hint}
            className="flex flex-col items-center gap-3.5 px-3 pb-4 pt-[18px] text-center"
          >
            <MiniPhone
              position={option.value}
              backgroundColorHex={form.backgroundColorHex}
            />
          </ChoiceCard>
        ))}
      </div>
    </div>
  );
}

/** The banner text on its colours, still or scrolling. */
function EffectSample({
  form,
  animated,
}: {
  form: CouponBannerForm;
  animated: boolean;
}) {
  const text = getBannerPlainText(form.bannerText) || form.couponCode;
  return (
    <span
      aria-hidden
      className="flex h-[26px] w-[150px] shrink-0 items-center overflow-hidden rounded-md px-2 text-[10px] max-md:w-[120px]"
      style={{
        backgroundColor: form.backgroundColorHex,
        color: form.foregroundColorHex,
      }}
    >
      {animated ? (
        <span className="flex animate-[scrolling-text_8s_linear_infinite] whitespace-nowrap motion-reduce:animate-none">
          <span className="pr-5">{text}</span>
          <span className="pr-5">{text}</span>
        </span>
      ) : (
        <span className="whitespace-nowrap">{text}</span>
      )}
    </span>
  );
}

export function CouponEffectPanel({ form, onChange }: PanelProps) {
  const { LL } = useI18nContext();
  const options = [
    {
      animated: false,
      label: LL.couponEditor.effect.none(),
      hint: LL.couponEditor.effect.noneHint(),
    },
    {
      animated: true,
      label: LL.couponEditor.effect.scrolling(),
      hint: LL.couponEditor.effect.scrollingHint(),
    },
  ];

  return (
    <div className="space-y-3">
      <Label className={FIELD_LABEL}>
        {LL.appCustomizationPage.couponBanner.animationLabel()}
      </Label>
      {options.map((option) => (
        <ChoiceCard
          key={String(option.animated)}
          pressed={form.animated === option.animated}
          onClick={() => onChange({ ...form, animated: option.animated })}
          label={option.label}
          hint={option.hint}
          className="flex min-h-[76px] flex-row-reverse items-center justify-between gap-3.5 px-4 py-3.5 text-left"
        >
          <EffectSample form={form} animated={option.animated} />
        </ChoiceCard>
      ))}
    </div>
  );
}
